/**
 * ADR-0240 Agency Portal v2 Slice 7 — minimal .xlsx reader.
 *
 * No npm dependency (`sheetjs` is frozen at a vulnerable version; the
 * fix lives on the vendor's own CDN, outside npm — and `exceljs` is
 * large). This file parses .xlsx files using only native browser APIs:
 *
 *   - `ArrayBuffer` + `DataView` to walk the ZIP's central directory;
 *   - `DecompressionStream('deflate-raw')` to inflate DEFLATE entries;
 *   - `DOMParser` to parse the shared-strings + sheet XML.
 *
 * Scope (spec §5.4):
 *   - First sheet only.
 *   - ≤ 2 MB compressed file, ≤ 20 MB per inflated entry, ≤ 5 000 rows,
 *     ≤ 50 columns.
 *   - Dates / formulas / numeric types are coerced to text (domains +
 *     emails are strings).
 *   - Reject encrypted / OLE .xls with a copy-friendly reason.
 *
 * Threat model: runs client-side in the agency's own browser on their
 * own file. The caps bound a hang. The caller is responsible for
 * putting the parsed grid through `prospect-import.ts`, which does all
 * the validation + dedupe.
 */

export const XLSX_MAX_BYTES = 2 * 1024 * 1024;
export const XLSX_MAX_ENTRY_BYTES = 20 * 1024 * 1024;
export const XLSX_MAX_ROWS = 5000;
export const XLSX_MAX_COLS = 50;

export type ReadXlsxResult =
  | { readonly ok: true; readonly grid: string[][] }
  | {
      readonly ok: false;
      readonly reason:
        | 'file_too_large'
        | 'not_zip'
        | 'legacy_xls'
        | 'encrypted'
        | 'decompress_unavailable'
        | 'entry_too_large'
        | 'malformed'
        | 'empty_workbook';
    };

/** Entry point. Pass a `File`, `Blob`, or `ArrayBuffer`; returns the
 *  parsed grid from the first sheet. */
export async function readXlsx(file: Blob | ArrayBuffer): Promise<ReadXlsxResult> {
  const size = file instanceof ArrayBuffer ? file.byteLength : file.size;
  if (size > XLSX_MAX_BYTES) return { ok: false, reason: 'file_too_large' };
  if (typeof DOMParser === 'undefined') {
    return { ok: false, reason: 'decompress_unavailable' };
  }
  const buf: ArrayBuffer =
    file instanceof ArrayBuffer
      ? file
      : typeof (file as Blob).arrayBuffer === 'function'
        ? await (file as Blob).arrayBuffer()
        // jsdom's Blob doesn't expose arrayBuffer; fall back to the
        // FileReader API available there.
        : await new Promise<ArrayBuffer>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result as ArrayBuffer);
            fr.onerror = () => reject(fr.error);
            fr.readAsArrayBuffer(file as Blob);
          });
  const dv = new DataView(buf);

  // The .xls (legacy OLE) magic is D0 CF 11 E0 A1 B1 1A E1 — hint
  // the user to save-as .xlsx.
  if (
    buf.byteLength >= 8
    && dv.getUint8(0) === 0xd0
    && dv.getUint8(1) === 0xcf
    && dv.getUint8(2) === 0x11
    && dv.getUint8(3) === 0xe0
  ) {
    return { ok: false, reason: 'legacy_xls' };
  }
  // ZIP magic PK\x03\x04.
  if (
    buf.byteLength < 4
    || dv.getUint8(0) !== 0x50
    || dv.getUint8(1) !== 0x4b
    || dv.getUint8(2) !== 0x03
    || dv.getUint8(3) !== 0x04
  ) {
    return { ok: false, reason: 'not_zip' };
  }

  // Walk Local-File headers one-by-one. .xlsx entries sit in order;
  // scan until we have every file we need. Security review
  // 2026-10-10 Minor 3: every length / offset read is bounds-
  // checked against `buf.byteLength` so a crafted ZIP cannot steer
  // the parser into an out-of-range slice (which would otherwise
  // throw later and surface as `malformed` — fine but let's refuse
  // explicitly at the first inconsistency).
  const entries = new Map<string, { offset: number; comp_size: number; method: number }>();
  let p = 0;
  while (p + 30 <= buf.byteLength) {
    const sig = dv.getUint32(p, true);
    if (sig !== 0x04034b50) break; // central-directory starts
    const method = dv.getUint16(p + 8, true);
    const comp_size = dv.getUint32(p + 18, true);
    const name_len = dv.getUint16(p + 26, true);
    const extra_len = dv.getUint16(p + 28, true);
    // Bounds check — header body (name + extra) must fit inside buf.
    const header_end = p + 30 + name_len + extra_len;
    if (header_end > buf.byteLength) return { ok: false, reason: 'malformed' };
    // Bounds check — advertised compressed payload must fit AND stay
    // within our XLSX_MAX_ENTRY_BYTES ceiling.
    if (comp_size > XLSX_MAX_ENTRY_BYTES) return { ok: false, reason: 'entry_too_large' };
    const data_offset = header_end;
    const data_end = data_offset + comp_size;
    if (data_end > buf.byteLength) return { ok: false, reason: 'malformed' };
    // Name length sanity — PK\x03\x04 headers are capped at 2^16-1
    // but a 65535-byte name is clearly adversarial. Clamp at a
    // conservative 1 KB; .xlsx entry names never exceed ~50 bytes.
    if (name_len > 1024) return { ok: false, reason: 'malformed' };
    const name = decodeAscii(buf, p + 30, name_len);
    entries.set(name, { offset: data_offset, comp_size, method });
    p = data_end;
  }

  const sharedStringsEntry = entries.get('xl/sharedStrings.xml');
  // Common path: pick xl/worksheets/sheet1.xml. If the workbook sorts
  // sheets differently, fallback to the first `xl/worksheets/sheet*`.
  let sheetName = 'xl/worksheets/sheet1.xml';
  if (!entries.has(sheetName)) {
    for (const name of entries.keys()) {
      if (/^xl\/worksheets\/sheet\d+\.xml$/.test(name)) {
        sheetName = name;
        break;
      }
    }
  }
  const sheetEntry = entries.get(sheetName);
  if (sheetEntry === undefined) return { ok: false, reason: 'empty_workbook' };

  const shared_strings =
    sharedStringsEntry === undefined
      ? []
      : await readSharedStrings(buf, sharedStringsEntry);

  if (shared_strings === null) return { ok: false, reason: 'malformed' };

  // Encryption marker (very rough) — `xl/encryption/EncryptionInfo` or
  // `EncryptedPackage` entries live in encrypted workbooks instead of
  // plain `xl/*`.
  for (const name of entries.keys()) {
    if (name.toLowerCase().includes('encryptedpackage')) {
      return { ok: false, reason: 'encrypted' };
    }
  }

  const sheet_xml = await inflateEntry(buf, sheetEntry);
  if (sheet_xml === null) return { ok: false, reason: 'malformed' };
  const grid = parseSheetXml(sheet_xml, shared_strings);
  return { ok: true, grid };
}

function decodeAscii(buf: ArrayBuffer, offset: number, length: number): string {
  const bytes = new Uint8Array(buf, offset, length);
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i] as number);
  return s;
}

async function inflateEntry(
  buf: ArrayBuffer,
  entry: { offset: number; comp_size: number; method: number },
): Promise<string | null> {
  // Security review 2026-10-10 Minor 4 — one try/catch around the
  // WHOLE body (slice construction, stream read, UTF-8 decode)
  // so a crafted ZIP that passes the outer bounds check but blows up
  // inside DecompressionStream / TextDecoder lands cleanly as
  // `malformed`, never as an unhandled rejection.
  try {
    // Belt: guard against zip-bomb (comp_size already checked at the
    // outer walk) and against a slice that exceeds buf.
    if (entry.comp_size > XLSX_MAX_ENTRY_BYTES) return null;
    if (entry.offset + entry.comp_size > buf.byteLength) return null;
    const slice = new Uint8Array(buf, entry.offset, entry.comp_size);
    let bytes: Uint8Array;
    if (entry.method === 0) {
      if (slice.byteLength > XLSX_MAX_ENTRY_BYTES) return null;
      bytes = slice;
    } else if (entry.method === 8) {
      if (typeof DecompressionStream === 'undefined') return null;
      const ds = new DecompressionStream('deflate-raw');
      const stream = new Blob([slice]).stream().pipeThrough(ds);
      const reader = stream.getReader();
      const chunks: Uint8Array[] = [];
      let total = 0;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          total += value.byteLength;
          if (total > XLSX_MAX_ENTRY_BYTES) return null;
          chunks.push(value);
        }
      }
      bytes = concat(chunks, total);
    } else {
      return null;
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return null;
  }
}

function concat(chunks: Uint8Array[], total: number): Uint8Array {
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

async function readSharedStrings(
  buf: ArrayBuffer,
  entry: { offset: number; comp_size: number; method: number },
): Promise<string[] | null> {
  const xml = await inflateEntry(buf, entry);
  if (xml === null) return null;
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const strings: string[] = [];
  // Each <si> may contain a plain <t> OR rich-text runs <r><t>...</t></r>.
  for (const si of Array.from(doc.getElementsByTagName('si'))) {
    const ts = si.getElementsByTagName('t');
    let s = '';
    for (let i = 0; i < ts.length; i += 1) s += ts[i]?.textContent ?? '';
    strings.push(s);
  }
  return strings;
}

function parseSheetXml(xml: string, shared_strings: readonly string[]): string[][] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const rows = doc.getElementsByTagName('row');
  const grid: string[][] = [];
  for (let i = 0; i < rows.length; i += 1) {
    if (grid.length >= XLSX_MAX_ROWS) break;
    const row = rows[i] as Element;
    const cells = row.getElementsByTagName('c');
    // Build a positional array keyed by column letter in cell `r=`.
    const row_cells: string[] = [];
    for (let j = 0; j < cells.length; j += 1) {
      const c = cells[j] as Element;
      const ref = c.getAttribute('r') ?? '';
      const col_idx = colIndexFromRef(ref);
      if (col_idx < 0 || col_idx >= XLSX_MAX_COLS) continue;
      const t = c.getAttribute('t');
      let value = '';
      if (t === 's') {
        // Shared string reference.
        const idx = Number(c.getElementsByTagName('v')[0]?.textContent ?? '0');
        value = shared_strings[idx] ?? '';
      } else if (t === 'inlineStr') {
        const is_tags = c.getElementsByTagName('t');
        for (let k = 0; k < is_tags.length; k += 1) {
          value += is_tags[k]?.textContent ?? '';
        }
      } else {
        value = c.getElementsByTagName('v')[0]?.textContent ?? '';
      }
      while (row_cells.length < col_idx) row_cells.push('');
      row_cells.push(value);
    }
    grid.push(row_cells);
  }
  return grid;
}

/** `A1` → 0, `B1` → 1, `AA1` → 26. */
function colIndexFromRef(ref: string): number {
  let n = 0;
  for (const ch of ref) {
    const code = ch.charCodeAt(0);
    if (code >= 65 && code <= 90) {
      n = n * 26 + (code - 64);
    } else if (code >= 97 && code <= 122) {
      n = n * 26 + (code - 96);
    } else {
      break;
    }
  }
  return n - 1;
}
